<script setup lang="ts">
import type { NavigationMenuItem } from '@nuxt/ui'

const toast = useToast()
const auth = useAuth()
useDashboard()

const open = ref(false)

const handleLogout = () => {
  auth.logout()
}

const links = [
  [
    {
      label: 'Manage',
      to: '/manage',
      icon: 'i-lucide-folder',
      defaultOpen: true,
      type: 'trigger',
      children: [
        {
          label: 'Documents',
          to: '/manage/documents',
          onSelect: () => {
            open.value = false
          },
        },
        {
          label: 'Prompts',
          to: '/manage/prompts',
          onSelect: () => {
            open.value = false
          },
        },
        {
          label: 'Presets',
          to: '/manage/presets',
          onSelect: () => {
            open.value = false
          },
        },
        {
          label: 'Policies',
          to: '/manage/policies',
          onSelect: () => {
            open.value = false
          },
        },
        {
          label: 'Lexicon',
          to: '/manage/lexicon',
          onSelect: () => {
            open.value = false
          },
        },
        {
          label: 'Accounts',
          to: '/manage/accounts',
          onSelect: () => {
            open.value = false
          },
        },
      ],
      onSelect: () => {
        open.value = false
      },
    },
    {
      label: 'Chat',
      to: '/chat',
      icon: 'i-lucide-message-circle',
      defaultOpen: true,
      type: 'trigger',
      children: [
        {
          label: 'Prompt Tuning',
          to: '/chat/prompt-tuning',
          onSelect: () => {
            open.value = false
          },
        },
        {
          label: 'RAG Chat',
          to: '/chat/rag',
          onSelect: () => {
            open.value = false
          },
        },
      ],
      onSelect: () => {
        open.value = false
      },
    },
    {
      label: 'Conversations',
      to: '/conversations',
      icon: 'i-lucide-messages-square',
      onSelect: () => {
        open.value = false
      },
    },
    {
      label: 'System',
      to: '/system',
      icon: 'i-lucide-settings',
      defaultOpen: true,
      type: 'trigger',
      children: [
        {
          label: 'API Keys',
          to: '/system/apikeys',
          onSelect: () => {
            open.value = false
          },
        },
        {
          label: 'Spend',
          to: '/system/spend',
          onSelect: () => {
            open.value = false
          },
        },
      ],
      onSelect: () => {
        open.value = false
      },
    },
  ],
  [
    {
      label: 'Logout',
      icon: 'i-lucide-log-out',
      onClick: handleLogout,
    },
  ],
] satisfies NavigationMenuItem[][]

const groups = computed(() => [
  {
    id: 'links',
    label: 'Go to',
    items: links.flat(),
  },
])

onMounted(async () => {
  const cookie = useCookie('cookie-consent')
  if (cookie.value === 'accepted') {
    return
  }

  toast.add({
    title: 'We use first-party cookies to enhance your experience on our website.',
    duration: 0,
    close: false,
    actions: [
      {
        label: 'Accept',
        color: 'neutral',
        variant: 'outline',
        onClick: () => {
          cookie.value = 'accepted'
        },
      },
      {
        label: 'Opt out',
        color: 'neutral',
        variant: 'ghost',
      },
    ],
  })
})
</script>

<template>
  <UDashboardGroup unit="rem">
    <UDashboardSidebar
      id="default"
      v-model:open="open"
      collapsible
      resizable
      class="sidebar-preset"
      :ui="{ footer: 'lg:border-t lg:border-inherit' }"
    >
      <template #header="{ collapsed }">
        <AppLogo :collapsed="collapsed" />
      </template>

      <template #default="{ collapsed }">
        <UDashboardSearchButton :collapsed="collapsed" class="bg-transparent ring-default" />

        <UNavigationMenu :collapsed="collapsed" :items="links[0]" orientation="vertical" tooltip popover />

        <UNavigationMenu :collapsed="collapsed" :items="links[1]" orientation="vertical" tooltip class="mt-auto" />
      </template>

      <template #footer="{ collapsed }">
        <UserMenu :collapsed="collapsed" />
      </template>
    </UDashboardSidebar>

    <UDashboardSearch :groups="groups" />

    <slot />
  </UDashboardGroup>
</template>
